import React, { useState } from 'react'
import classnames from 'classnames'
import { Form } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import styles from './Branding.module.scss'
import { BRANDING_ACCOUNT_SCOPE } from './brandingConstants'

const DISPLAY_NAME_MAX_LENGTH = 40

interface DisplayNameSectionProps {
  accountName: string
  displayName: string | null
  disabled: boolean
  onSave: (displayName: string | null) => Promise<boolean>
}

export const DisplayNameSection: React.FC<DisplayNameSectionProps> = ({ accountName, displayName, disabled, onSave }) => {
  const prefill = displayName ?? accountName.slice(0, DISPLAY_NAME_MAX_LENGTH)
  const [mode, setMode] = useState<'account' | 'custom'>(displayName != null ? 'custom' : 'account')
  const [draft, setDraft] = useState(prefill)

  // Re-sync with the saved value when it changes (e.g. the account query refetched after a save).
  const [lastSaved, setLastSaved] = useState(displayName)
  if (lastSaved !== displayName) {
    setLastSaved(displayName)
    setMode(displayName != null ? 'custom' : 'account')
    setDraft(displayName ?? accountName.slice(0, DISPLAY_NAME_MAX_LENGTH))
  }

  const selectAccountName = async () => {
    setMode('account')
    if (displayName != null && !(await onSave(null))) {
      setMode('custom')
    }
  }

  const cancel = () => {
    if (displayName == null) {
      setMode('account')
    }
    setDraft(prefill)
  }

  const selectCustom = () => {
    setMode('custom')
    setDraft(prefill)
  }

  const trimmedDraft = draft.trim()
  const isDirty = draft !== prefill

  return (
    <div className='d-flex flex-column gap-2'>
      <div className='d-flex flex-column'>
        <h6 className='zocks-text-gray-900'>Display name</h6>
        <p className='paragraph-small zocks-text-gray-700 m-0'>Your account name in Zocks stays the same.</p>
      </div>
      <ScopedBlock scope={BRANDING_ACCOUNT_SCOPE} missingScopeAction={MissingScopeAction.DISABLE}>
        <Form.Check
          type='radio'
          id='branding-display-name-account'
          className='mt-2'
          name='branding-display-name'
          checked={mode === 'account'}
          disabled={disabled}
          onChange={selectAccountName}
          label={
            <div className='d-flex flex-column'>
              <span className='paragraph-medium zocks-text-gray-900'>Use account name</span>
              <span className='paragraph-small zocks-text-gray-600'>{accountName}</span>
            </div>
          }
        />
        <Form.Check
          type='radio'
          id='branding-display-name-custom'
          className='mt-2'
          name='branding-display-name'
          checked={mode === 'custom'}
          disabled={disabled}
          onChange={selectCustom}
          label={
            <div className='d-flex flex-column'>
              <span className='paragraph-medium zocks-text-gray-900'>Use a different name</span>
              {mode === 'custom' ? <span className='paragraph-small zocks-text-gray-600'>Shorter names fit better on client-facing pages.</span> : null}
            </div>
          }
        />
        {mode === 'custom' ? (
          <div className='d-flex align-items-center gap-2 ps-4'>
            <div className='position-relative'>
              <Form.Control
                type='text'
                aria-label='Display name'
                className={classnames(styles.nameInput, 'zocks-border-color-gray-500')}
                value={draft}
                maxLength={DISPLAY_NAME_MAX_LENGTH}
                disabled={disabled}
                onChange={(e) => setDraft(e.currentTarget.value)}
              />
              <span className={classnames(styles.charCounter, 'paragraph-small zocks-text-gray-600')}>
                {draft.length}/{DISPLAY_NAME_MAX_LENGTH}
              </span>
            </div>
            <Button variant='secondary' className='button-medium zocks-px-0.75' disabled={disabled || !isDirty} onClick={cancel}>
              Cancel
            </Button>
            <Button
              variant='primary'
              className='button-medium'
              disabled={disabled || trimmedDraft.length === 0 || trimmedDraft === displayName}
              onClick={() => onSave(trimmedDraft)}
            >
              Save
            </Button>
          </div>
        ) : null}
      </ScopedBlock>
    </div>
  )
}
